/**
 * Shared save helpers for the FB workspace. Centralizes the "ensure parent
 * exists on Meta before saving the child" pattern so both BaseAdSetForm and
 * AdFormPanel can reuse it without duplicating logic.
 */
import toast from 'react-hot-toast';
import { facebookCampaignsService } from '@/services/facebook-campaigns.service';
import type { WorkspaceCampaign } from '@/contexts/FbWorkspaceContext';

/**
 * If the campaign already has an apiId, returns it. Otherwise validates
 * required campaign fields, POSTs to Meta, locks the campaign locally, and
 * returns the new apiId. Returns null on validation/network failure (after
 * showing a toast).
 */
export async function ensureCampaignSaved(
  campaign: WorkspaceCampaign,
  lockCampaign: (apiId: string, metaId?: string) => void,
  expectedAccountUid?: string,
): Promise<string | null> {
  if (campaign.apiId) return campaign.apiId;

  const cf = campaign.formData as Record<string, string>;
  const cName = cf.name ?? '';
  const cObjective = cf.objective ?? campaign.objective ?? '';
  if (!cName || !cObjective) {
    toast.error('Add a campaign name + pick an objective first');
    return null;
  }

  const cBudgetAt = (cf.budget_at ?? 'campaign') as 'campaign' | 'adset';
  const payload: Record<string, unknown> = {
    name: cName,
    objective: cObjective,
    buying_type: cf.buying_type ?? 'AUCTION',
    status: 'PAUSED',
    expected_account_uid: expectedAccountUid,
    special_ad_categories: cf.special_ad_categories ? JSON.parse(cf.special_ad_categories) : [],
  };
  if (cf.bid_strategy) payload.bid_strategy = cf.bid_strategy;
  if (cBudgetAt === 'campaign') {
    const cBudgetKind = (cf.budget_kind ?? 'daily') as 'daily' | 'lifetime';
    const cBudget = cf.budget ?? cf.daily_budget ?? cf.lifetime_budget ?? '';
    if (cBudget) {
      const minor = String(Math.round(parseFloat(cBudget) * 100));
      if (cBudgetKind === 'daily') payload.daily_budget = minor;
      else payload.lifetime_budget = minor;
    }
  }
  if (cf.spend_cap) payload.spend_cap = String(Math.round(parseFloat(cf.spend_cap) * 100));
  if (cf.start_time) payload.start_time = cf.start_time;
  if (cf.stop_time) payload.stop_time = cf.stop_time;

  try {
    const res = await facebookCampaignsService.create(
      payload as unknown as Parameters<typeof facebookCampaignsService.create>[0],
    );
    lockCampaign(res.id, res.id);
    toast.success('Campaign created on Meta');
    return res.id;
  } catch {
    // Axios interceptor already toasted the backend message; avoid duplicates.
    return null;
  }
}
